import type { ComponentPropsWithoutRef, ReactNode } from 'react';

function cx(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

type Variant = 'pos' | 'warn' | 'neg' | 'acc' | 'vio';

export function ConsolePanel({ className, ...props }: ComponentPropsWithoutRef<'section'>) {
  return <section className={cx('rl-panel', className)} {...props} />;
}

export function ConsolePanelHeader({
  title,
  muted,
  right,
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<'header'> & {
  title?: ReactNode;
  muted?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <header className={cx('rl-panel-h', className)} {...props}>
      {title ? <h3>{title}</h3> : null}
      {muted ? <span className="mut">{muted}</span> : null}
      {children}
      {right ? <div className="right">{right}</div> : null}
    </header>
  );
}

export function ConsoleButton({
  variant,
  className,
  ...props
}: ComponentPropsWithoutRef<'button'> & {
  variant?: 'pri' | 'gho' | 'ghost-quiet' | 'rl-ai-entry';
}) {
  return <button className={cx('rl-btn', variant, className)} type="button" {...props} />;
}

export function ConsoleChip({
  variant,
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<'span'> & {
  variant?: Variant;
}) {
  return (
    <span className={cx('rl-chip', variant, className)} {...props}>
      <span className="d" aria-hidden="true" />
      {children}
    </span>
  );
}

export function ConsoleTabs({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div className={cx('rl-tabs', className)} role="tablist" {...props} />;
}

export function ConsoleTab({
  active,
  className,
  ...props
}: ComponentPropsWithoutRef<'button'> & {
  active?: boolean;
}) {
  return (
    <button
      className={cx('rl-tab', active && 'on', className)}
      role="tab"
      aria-selected={active}
      type="button"
      {...props}
    />
  );
}

export function ConsoleStageStrip({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div className={cx('rl-stage-strip', className)} {...props} />;
}

export function ConsoleStage({
  number,
  icon,
  name,
  date,
  complete,
  current,
  className,
  ...props
}: ComponentPropsWithoutRef<'div'> & {
  number: ReactNode;
  icon?: ReactNode;
  name: ReactNode;
  date?: ReactNode;
  complete?: boolean;
  current?: boolean;
}) {
  return (
    <div className={cx('rl-stage', complete && 'done', current && 'cur', className)} {...props}>
      <div className="st-n">{number}</div>
      {icon ? <div className="st-ic">{icon}</div> : null}
      <div className="st-nm">{name}</div>
      {date ? <div className="st-dt">{date}</div> : null}
      {complete ? (
        <div className="st-chk">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m5 12 4 4 10-10" />
          </svg>
        </div>
      ) : null}
    </div>
  );
}

export function ConsoleTable({ className, ...props }: ComponentPropsWithoutRef<'table'>) {
  return <table className={cx('rl-tbl', className)} {...props} />;
}

export function ConsoleRow({
  icon,
  title,
  subtitle,
  right,
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<'div'> & {
  icon?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className={cx('rl-row', className)} {...props}>
      {icon ? <div className="r-ic">{icon}</div> : null}
      {title || subtitle ? (
        <div>
          {title ? <div className="r-t">{title}</div> : null}
          {subtitle ? <div className="r-s">{subtitle}</div> : null}
        </div>
      ) : null}
      {children}
      {right ? <div className="r-r">{right}</div> : null}
    </div>
  );
}

export function ConsoleSignal({
  variant,
  icon,
  title,
  body,
  meta,
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<'article'> & {
  variant?: 'acc';
  icon?: ReactNode;
  title?: ReactNode;
  body?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <article className={cx('rl-signal', variant, className)} {...props}>
      {icon ? <div className="s-ic">{icon}</div> : null}
      <div>
        {title ? <div className="s-t">{title}</div> : null}
        {body ? <div className="s-p">{body}</div> : null}
        {meta ? <div className="s-meta">{meta}</div> : null}
        {children}
      </div>
    </article>
  );
}

export function ConsoleRing({
  value,
  label,
  status,
  className,
  ...props
}: ComponentPropsWithoutRef<'div'> & {
  value: ReactNode;
  label?: ReactNode;
  status?: ReactNode;
}) {
  return (
    <div className={cx('rl-ring', className)} {...props}>
      <svg viewBox="0 0 64 64" aria-hidden="true">
        <circle className="rr-base" cx="32" cy="32" r="27" />
        <circle
          className="rr-prog"
          cx="32"
          cy="32"
          r="27"
          pathLength="100"
          strokeDasharray="72 100"
        />
      </svg>
      <div className="rr-center">
        <div className="v">{value}</div>
        {label ? <div className="l">{label}</div> : null}
        {status ? <div className="rr-st">{status}</div> : null}
      </div>
    </div>
  );
}

export function ConsoleBarList({ className, ...props }: ComponentPropsWithoutRef<'div'>) {
  return <div className={cx('rl-barlist', className)} {...props} />;
}

export function ConsoleBarRow({
  label,
  value,
  width,
  color = 'var(--accent)',
}: {
  label: ReactNode;
  value: ReactNode;
  width: number;
  color?: string;
}) {
  return (
    <div className="rl-barrow">
      <div className="bl">{label}</div>
      <div className="rl-track">
        <span style={{ width: `${width}%`, background: color }} />
      </div>
      <div className="bv">{value}</div>
    </div>
  );
}

export function ConsoleLegend({
  items,
  className,
  ...props
}: ComponentPropsWithoutRef<'div'> & {
  items: Array<{ label: ReactNode; color: string }>;
}) {
  return (
    <div className={cx('rl-legend', className)} {...props}>
      {items.map((item, index) => (
        <span className="lg" key={index}>
          <i style={{ background: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}
